/**
 * Bots (BotSvr) — GM4 (feature bots_manage).
 *  - Aba Dashboard: quantos on / cadastrados, Iniciar (canais x quantidade, 1 por canal a cada N s) /
 *    Desligar todos, seletor de mapa com contagem, mapa do mundo com os bots AO VIVO (heartbeat do bot)
 *    e lista lateral — a seleção destaca nos dois.
 *  - Aba Bots: contas botsvr* + 1º personagem (o que o bot loga); clicar abre equipamento/inventário
 *    do char (ItemsView — edição de slot GM4 via ItemSlotEditModal) e o poder de combate.
 * Posição no mapa: as 256 células do mundo ocupam o quadrado [37,475) do PNG de 512px (ver mapPos).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot, RefreshCw, Play, Power, X, Loader2, CheckSquare, Square, Zap, ChevronLeft, Circle, LayoutDashboard,
  MessageCircle, Navigation, MapPin, Send,
} from 'lucide-react';
import {
  listBots, listOnlineBots, startBots, stopBots,
  listBotWhispers, sendBotWhisper, moveBots, teleportBots,
  type BotAccountRow, type OnlineBotRow, type BotWhisperRow,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { ItemsView } from './ItemsView';
import { formatClass, NATIONS_PT } from './labels';

const WORLDS: { id: number; name: string }[] = [
  { id: 1, name: 'Tundra Infame' },
  { id: 2, name: 'Deserto Lamentação' },
  { id: 3, name: 'Floresta do Desespero' },
  { id: 4, name: 'Porto Lux' },
  { id: 5, name: 'Forte Ruína' },
  { id: 6, name: 'Região dos Lagos' },
  { id: 7, name: 'Santuário Profano' },
  { id: 8, name: 'Elo Perdido' },
  { id: 9, name: 'Floresta Mutante' },
  { id: 10, name: 'Pontus Ferrum' },
  { id: 11, name: 'Portal Infernus' },
  { id: 12, name: 'Vestígio Arcano' },
  { id: 22, name: 'Senillinea' },
];
const CHANNELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12];
const STATE_LABEL: Record<number, string> = { 1: 'logando', 2: 'no mundo', 3: 'andando', 4: 'dançando', 5: 'relogando' };
const POLL_MS = 5000;
// Área jogável dentro do PNG (512px, textura do client): o quadrado [37,475) — a borda em volta é
// enfeite desenhado fora do mundo. As 256 células do mundo cobrem esses 438px; y cresce pra cima
// (client CMapDialog::GetMapPos: left + x*RATIO/256, bottom - y*RATIO/256).
const MAP_TEX = 512, MAP_LEFT = 37, MAP_SPAN = 438;
const mapPos = (x: number, y: number) => ({
  left: `${((MAP_LEFT + (x / 256) * MAP_SPAN) / MAP_TEX) * 100}%`,
  top: `${((MAP_LEFT + MAP_SPAN - (y / 256) * MAP_SPAN) / MAP_TEX) * 100}%`,
});
// Inverso do mapPos: ponto clicado no mapa -> célula do mundo (null = fora da área jogável).
const cellFromClick = (e: React.MouseEvent<HTMLElement>) => {
  const r = e.currentTarget.getBoundingClientRect();
  const px = ((e.clientX - r.left) / r.width) * MAP_TEX, py = ((e.clientY - r.top) / r.height) * MAP_TEX;
  const x = Math.round(((px - MAP_LEFT) / MAP_SPAN) * 256), y = Math.round(((MAP_LEFT + MAP_SPAN - py) / MAP_SPAN) * 256);
  return x < 0 || x > 255 || y < 0 || y > 255 ? null : { x, y };
};

type PickMode = 'move' | 'teleport';

const fmtUptime = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
};
const errMsg = (e: any, d: string) => e?.detail || e?.message || d;

export default function BotsPage() {
  const { notify } = useToast();
  const [tab, setTab] = useState<'dashboard' | 'bots'>('dashboard');
  const [accounts, setAccounts] = useState<BotAccountRow[]>([]);
  const [online, setOnline] = useState<OnlineBotRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    try { setAccounts(await listBots()); } catch (e) { notify('error', errMsg(e, 'Erro ao carregar os bots')); }
  }, [notify]);
  const fetchOnline = useCallback(async () => {
    try { setOnline(await listOnlineBots()); } catch { /* poll silencioso */ }
  }, []);
  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchAccounts(), fetchOnline()]);
    setLoading(false);
  }, [fetchAccounts, fetchOnline]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(fetchOnline, POLL_MS);
    return () => clearInterval(t);
  }, [fetchOnline]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Bot className="text-[#00e1d7]" size={26} /> Bots
          </h1>
          <p className="text-sm text-white/65 mt-1">BotSvr V2 — contas botsvr*, posição ao vivo e controle</p>
        </div>
        <button onClick={refresh} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7] hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {/* Tabs — mesmo padrão do Guard */}
      <div className="flex gap-1 mb-5 surface-card rounded-xl p-1 w-fit overflow-x-auto">
        <TabBtn active={tab === 'dashboard'} onClick={() => setTab('dashboard')}>
          <LayoutDashboard size={14} /> Dashboard
        </TabBtn>
        <TabBtn active={tab === 'bots'} onClick={() => setTab('bots')}>
          <Bot size={14} /> Bots
        </TabBtn>
      </div>

      {tab === 'dashboard'
        ? <DashboardTab accounts={accounts} online={online} onChanged={refresh} />
        : <BotsTab accounts={accounts} online={online} loading={loading} />}
    </div>
  );
}

/* ───────────────────────── Dashboard ───────────────────────── */

function DashboardTab({ accounts, online, onChanged }: {
  accounts: BotAccountRow[]; online: OnlineBotRow[]; onChanged: () => void;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [channel, setChannel] = useState(1);
  const [world, setWorld] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [hover, setHover] = useState<number | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Mover/Teleportar: guarda os bots no início (trocar de mapa no teleporte limpa a seleção, não o alvo)
  const [pick, setPick] = useState<{ mode: PickMode; ids: number[]; fromWorld: number } | null>(null);
  const [cursorCell, setCursorCell] = useState<{ x: number; y: number } | null>(null);
  const [acting, setActing] = useState(false);
  const [whisperFor, setWhisperFor] = useState<OnlineBotRow | null>(null);

  const registered = accounts.filter(a => a.characterIdx).length;
  // o mapa é por CANAL + mapa — nunca mistura canais
  const countByChannel = useMemo(() => {
    const m: Record<number, number> = {};
    for (const b of online) m[b.channel] = (m[b.channel] || 0) + 1;
    return m;
  }, [online]);
  const countByWorld = useMemo(() => {
    const m: Record<number, number> = {};
    for (const b of online) if (b.channel === channel) m[b.mapId] = (m[b.mapId] || 0) + 1;
    return m;
  }, [online, channel]);
  const inWorld = useMemo(
    () => online.filter(b => b.channel === channel && b.mapId === world).sort((a, b) => a.name.localeCompare(b.name)),
    [online, channel, world]);

  // seleção só vale pro canal+mapa atual; some quem saiu dele
  useEffect(() => { setSelected(new Set()); }, [channel, world]);
  useEffect(() => {
    setSelected(prev => {
      const ids = new Set(inWorld.map(b => b.characterIdx));
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [inWorld]);

  const toggle = (id: number) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = inWorld.length > 0 && inWorld.every(b => selected.has(b.characterIdx));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(inWorld.map(b => b.characterIdx)));
  const selectedBots = inWorld.filter(b => selected.has(b.characterIdx));

  // Mover só vale no mapa atual dos bots; trocar de canal cancela qualquer um. Esc cancela.
  useEffect(() => { setPick(p => (p && p.mode === 'move' && world !== p.fromWorld ? null : p)); }, [world]);
  useEffect(() => { setPick(null); }, [channel]);
  useEffect(() => {
    if (!pick) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPick(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pick]);

  const startPick = (mode: PickMode) => setPick({ mode, ids: selectedBots.map(b => b.characterIdx), fromWorld: world });

  const runPick = async (cell: { x: number; y: number }) => {
    if (!pick || acting) return;
    setActing(true);
    try {
      const r = pick.mode === 'move'
        ? await moveBots({ characterIdxs: pick.ids, x: cell.x, y: cell.y })
        : await teleportBots({ characterIdxs: pick.ids, world, x: cell.x, y: cell.y });
      notify('success', r.message);
      setPick(null);
    } catch (e) { notify('error', errMsg(e, pick.mode === 'move' ? 'Erro ao mover' : 'Erro ao teleportar')); }
    finally { setActing(false); }
  };

  const doStop = async () => {
    const ok = await confirm({
      title: 'Desligar TODOS os bots?',
      description: 'Encerra os processos do BotSvr V2 (inclui o teste do canal 12). Os bots saem do jogo em alguns segundos.',
      tone: 'danger', confirmLabel: 'Desligar bots',
    });
    if (!ok) return;
    setStopping(true);
    try {
      const r = await stopBots();
      notify('success', r.message);
      setTimeout(onChanged, 4000);
    } catch (e) { notify('error', errMsg(e, 'Erro ao desligar')); }
    finally { setStopping(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-stretch gap-3 mb-4">
        <StatCard label="Bots on" value={online.length} accent="text-emerald-400" />
        <StatCard label="Bots cadastrados (em char)" value={registered} accent="text-[#00e1d7]" />
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setStartOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition cursor-pointer">
            <Play size={15} /> Iniciar bot
          </button>
          <button onClick={doStop} disabled={stopping}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition cursor-pointer disabled:opacity-50">
            {stopping ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />} Desligar bots
          </button>
        </div>
      </div>

      {/* seletor de canal */}
      <div className="surface-card rounded-xl p-2 mb-2 flex gap-1.5 overflow-x-auto">
        {CHANNELS.map(c => {
          const n = countByChannel[c] || 0;
          return (
            <button key={c} onClick={() => setChannel(c)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition border whitespace-nowrap
                ${channel === c ? 'bg-[#00e1d7]/15 text-[#00e1d7] border-[#00e1d7]/40'
                                : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
              Canal {c} <span className={n ? 'text-emerald-400' : 'text-white/30'}>({n})</span>
            </button>
          );
        })}
      </div>

      {/* seletor de mapa (contagem do canal escolhido) */}
      <div className="surface-card rounded-xl p-2 mb-4 flex gap-1.5 overflow-x-auto">
        {WORLDS.map(w => {
          const n = countByWorld[w.id] || 0;
          return (
            <button key={w.id} onClick={() => setWorld(w.id)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition border whitespace-nowrap
                ${world === w.id ? 'bg-[#00e1d7]/15 text-[#00e1d7] border-[#00e1d7]/40'
                                 : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
              {w.name} <span className={n ? 'text-emerald-400' : 'text-white/30'}>({n})</span>
            </button>
          );
        })}
      </div>

      {/* mapa no tamanho real do PNG (512px) + folga; o resto da largura vai pra lista */}
      <div className="grid grid-cols-1 lg:grid-cols-[536px_minmax(0,1fr)] gap-4">
        {/* mapa */}
        <div className="surface-card rounded-xl p-3">
          <div className={`relative w-full max-w-[512px] mx-auto aspect-square rounded-lg overflow-hidden bg-black/40 select-none
                           ${pick ? 'cursor-crosshair ring-2 ring-amber-400/60' : ''}`}
               onMouseMove={pick ? (e) => setCursorCell(cellFromClick(e)) : undefined}
               onMouseLeave={() => setCursorCell(null)}
               onClick={pick ? (e) => { const c = cellFromClick(e); if (c) runPick(c); } : undefined}>
            <img src={`/maps/world${world}.png`} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
            {pick && (
              <div className="absolute top-2 left-2 right-2 z-30 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-black/85 border border-amber-400/40 text-[11px] text-white"
                   onClick={(e) => e.stopPropagation()}>
                {acting ? <Loader2 size={13} className="animate-spin text-amber-300" />
                        : pick.mode === 'move' ? <Navigation size={13} className="text-amber-300" /> : <MapPin size={13} className="text-amber-300" />}
                <span className="font-bold">
                  {pick.mode === 'move' ? 'Mover' : 'Teleportar'} {pick.ids.length} bot(s):
                </span>
                <span className="text-white/70 truncate">
                  {pick.mode === 'move' ? 'clique no destino' : 'escolha o mapa acima (opcional) e clique no destino'}
                  {cursorCell && ` · (${cursorCell.x},${cursorCell.y})`}
                </span>
                <button onClick={() => setPick(null)} className="ml-auto text-white/60 hover:text-white cursor-pointer" title="Cancelar (Esc)">
                  <X size={14} />
                </button>
              </div>
            )}
            {pick && cursorCell && (
              <span className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-300 pointer-events-none z-20"
                    style={mapPos(cursorCell.x, cursorCell.y)} />
            )}
            {inWorld.map(b => {
              const sel = selected.has(b.characterIdx);
              const hov = hover === b.characterIdx;
              return (
                <button key={b.characterIdx}
                  onClick={() => toggle(b.characterIdx)}
                  onMouseEnter={() => setHover(b.characterIdx)} onMouseLeave={() => setHover(null)}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer ${pick ? 'pointer-events-none' : ''}`}
                  style={{ ...mapPos(b.x, b.y), zIndex: sel || hov ? 20 : 10 }}>
                  <img src={sel ? '/maps/me.png' : '/maps/party.png'} alt=""
                       className={sel ? 'w-6 h-6 drop-shadow-[0_0_4px_#00e1d7]' : 'w-3.5 h-3.5'} />
                  {(hov || sel) && (
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-1.5 py-0.5 rounded bg-black/85 border border-white/15 text-[10px] text-white whitespace-nowrap pointer-events-none">
                      {b.name} · ({b.x},{b.y})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* lista */}
        <div className="surface-card rounded-xl p-3 flex flex-col min-h-0 lg:h-[536px]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-white/80">{inWorld.length} online · Canal {channel}</p>
            <button onClick={toggleAll} disabled={!inWorld.length}
              className="flex items-center gap-1.5 text-[11px] font-bold text-[#00e1d7] hover:text-white cursor-pointer disabled:opacity-40">
              {allSelected ? <CheckSquare size={13} /> : <Square size={13} />} Selecionar todos
            </button>
          </div>
          {inWorld.length === 0 && <p className="text-xs text-white/40 py-6 text-center">Nenhum bot neste mapa</p>}
          <div className="flex-1 min-h-0 overflow-y-auto max-h-[480px] lg:max-h-none grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-1 content-start pr-1">
            {inWorld.map(b => {
              const sel = selected.has(b.characterIdx);
              return (
                <button key={b.characterIdx} onClick={() => toggle(b.characterIdx)}
                  onMouseEnter={() => setHover(b.characterIdx)} onMouseLeave={() => setHover(null)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg border transition cursor-pointer
                    ${sel ? 'bg-[#00e1d7]/15 border-[#00e1d7]/40'
                          : hover === b.characterIdx ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white/[0.02] border-white/[0.05]'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-bold truncate ${sel ? 'text-[#00e1d7]' : 'text-white'}`}>{b.name}</span>
                    {b.unread > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-bold"
                            title={`${b.unread} sussurro(s) não lido(s)`}>
                        <MessageCircle size={9} /> {b.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-white/40 flex justify-between">
                    <span>{STATE_LABEL[b.state] || `estado ${b.state}`} · ({b.x},{b.y})</span>
                    <span>{fmtUptime(b.uptime)}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* barra de ações — fixa no rodapé da lista enquanto houver bot selecionado */}
          {selectedBots.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/[0.08] flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold text-white/60 mr-auto">{selectedBots.length} selecionado(s)</span>
              {selectedBots.length === 1 && (
                <button onClick={() => setWhisperFor(selectedBots[0])}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 transition cursor-pointer">
                  <MessageCircle size={13} /> Sussurros
                  {selectedBots[0].unread > 0 && (
                    <span className="px-1.5 rounded-full bg-amber-400 text-black text-[10px]">{selectedBots[0].unread}</span>
                  )}
                </button>
              )}
              <button onClick={() => startPick('move')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer
                  ${pick?.mode === 'move' ? 'bg-amber-400/20 border-amber-400/50 text-amber-300'
                                          : 'bg-[#00e1d7]/10 border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20'}`}>
                <Navigation size={13} /> Mover
              </button>
              <button onClick={() => startPick('teleport')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer
                  ${pick?.mode === 'teleport' ? 'bg-amber-400/20 border-amber-400/50 text-amber-300'
                                              : 'bg-[#00e1d7]/10 border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20'}`}>
                <MapPin size={13} /> Teleportar
              </button>
            </div>
          )}
        </div>
      </div>

      {whisperFor && <WhisperModal bot={whisperFor} onClose={() => setWhisperFor(null)} />}

      <StartModal open={startOpen} onClose={() => setStartOpen(false)} initialChannel={channel}
        free={accounts.filter(a => a.characterIdx && !a.online && a.accountId !== 'botsvr1').length}
        onStarted={() => setTimeout(onChanged, 3000)} />
    </div>
  );
}

/* Sussurros do bot (memória do hub). Quem responde é a STAFF — o bot nunca responde sozinho.
   Clicar num recebido preenche o destino com quem mandou. Abrir marca os recebidos como lidos. */
function WhisperModal({ bot, onClose }: { bot: OnlineBotRow; onClose: () => void }) {
  const { notify } = useToast();
  const [rows, setRows] = useState<BotWhisperRow[]>([]);
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setRows(await listBotWhispers(bot.characterIdx)); } catch { /* poll silencioso */ }
  }, [bot.characterIdx]);
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);
  // destino padrão = último que mandou
  useEffect(() => {
    if (to) return;
    const last = [...rows].reverse().find(r => r.dir === 'in');
    if (last) setTo(last.name);
  }, [rows, to]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [rows.length]);

  const send = async () => {
    if (!to.trim() || !text.trim() || sending) return;
    setSending(true);
    try {
      const w = await sendBotWhisper(bot.characterIdx, { to: to.trim(), text: text.trim() });
      setRows(r => [...r, w]);
      setText('');
      textRef.current?.focus();
    } catch (e) { notify('error', errMsg(e, 'Erro ao enviar o sussurro')); }
    finally { setSending(false); }
  };

  const hhmm = (ts: number) => new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const input = 'bg-[#0a0e14] border border-white/[0.08] rounded-lg px-2.5 py-2 text-sm text-white outline-none focus:border-purple-400/50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-5 w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <MessageCircle size={16} className="text-purple-300" /> Sussurros
            <span className="text-white/45 font-normal">— {bot.name} · Canal {bot.channel}</span>
          </h3>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <div ref={listRef} className="flex-1 min-h-[220px] max-h-[420px] overflow-y-auto space-y-1.5 pr-1 mb-3">
          {rows.length === 0 && <p className="text-xs text-white/40 text-center py-10">Nenhum sussurro ainda</p>}
          {rows.map(r => r.dir === 'in' ? (
            <button key={r.id} onClick={() => { setTo(r.name); textRef.current?.focus(); }}
              title={`Responder a ${r.name}`}
              className="block w-[85%] text-left px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] hover:border-purple-400/40 cursor-pointer">
              <span className="text-[11px] font-bold text-purple-300">{r.name}</span>
              <span className="text-[10px] text-white/35 ml-2">{hhmm(r.ts)}</span>
              <p className="text-sm text-white/90 break-words">{r.text}</p>
            </button>
          ) : (
            <div key={r.id} className="ml-auto w-[85%] px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <span className="text-[11px] font-bold text-white/60">→ {r.name}</span>
              <span className="text-[10px] text-white/35 ml-2">{hhmm(r.ts)}</span>
              <p className="text-sm text-white/90 break-words">{r.text}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Personagem" maxLength={16}
                 className={`${input} w-36`} />
          <input ref={textRef} value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensagem" maxLength={200}
                 onKeyDown={(e) => { if (e.key === 'Enter') send(); }} className={`${input} flex-1 min-w-0`} />
          <button onClick={send} disabled={sending || !to.trim() || !text.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/15 border border-purple-500/35 text-purple-300 text-xs font-bold hover:bg-purple-500/25 cursor-pointer disabled:opacity-50">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar
          </button>
        </div>
        <p className="text-[10px] text-white/35 mt-2">O bot envia o sussurro dentro do jogo. Clique numa mensagem recebida para responder a quem mandou.</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="surface-card rounded-xl px-5 py-3 min-w-[180px]">
      <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">{label}</p>
      <p className={`text-3xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

function StartModal({ open, onClose, free, onStarted, initialChannel }: {
  open: boolean; onClose: () => void; free: number; onStarted: () => void; initialChannel: number;
}) {
  const { notify } = useToast();
  const [chans, setChans] = useState<Set<number>>(new Set());
  const [perChannel, setPerChannel] = useState(5);
  const [delaySec, setDelaySec] = useState(300);
  const [busy, setBusy] = useState(false);

  // abre com o canal que está sendo visto no dashboard já marcado
  useEffect(() => { if (open) setChans(new Set([initialChannel])); }, [open, initialChannel]);

  if (!open) return null;
  const total = chans.size * perChannel;
  const lastMin = chans.size ? Math.round(((total - 1) * (delaySec / chans.size)) / 60) : 0;

  const toggle = (c: number) => setChans(prev => {
    const n = new Set(prev);
    if (n.has(c)) n.delete(c); else n.add(c);
    return n;
  });

  const submit = async () => {
    if (!chans.size) { notify('error', 'Escolha ao menos 1 canal'); return; }
    setBusy(true);
    try {
      const r = await startBots({ channels: [...chans].sort((a, b) => a - b), perChannel, delaySec });
      notify('success', r.message);
      onStarted();
      onClose();
    } catch (e) { notify('error', errMsg(e, 'Erro ao iniciar')); }
    finally { setBusy(false); }
  };

  const num = 'w-full bg-[#0a0e14] border border-white/[0.06] rounded px-2 py-1.5 text-sm text-white font-mono';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="surface-card rounded-2xl p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white flex items-center gap-2"><Play size={16} className="text-emerald-400" /> Iniciar bots</h3>
          <button onClick={onClose} disabled={busy} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <p className="text-[11px] text-white/50 mb-1.5">Canais (qualquer um de 1 a 10 e o 12)</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {CHANNELS.map(c => (
            <button key={c} onClick={() => toggle(c)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer border transition
                ${chans.has(c) ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40'
                               : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
              Canal {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] text-white/50">Quantos por canal</span>
            <input type="number" min={1} max={20} value={perChannel}
                   onChange={(e) => setPerChannel(Math.max(1, Math.min(20, Number(e.target.value) || 1)))} className={num} />
          </label>
          <label className="block">
            <span className="text-[11px] text-white/50">Intervalo (s) — 1 por canal</span>
            <input type="number" min={5} max={3600} value={delaySec}
                   onChange={(e) => setDelaySec(Math.max(5, Math.min(3600, Number(e.target.value) || 5)))} className={num} />
          </label>
        </div>

        <p className={`text-[11px] mt-3 leading-relaxed ${total > free ? 'text-red-400' : 'text-white/45'}`}>
          {total} bot(s) no total · {free} conta(s) livre(s)
          {chans.size > 0 && ` · cada canal ganha 1 bot a cada ${delaySec}s (último em ~${lastMin} min)`}.
          Modo AFK, nascem na Tundra.
        </p>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-2 rounded-lg bg-white/[0.05] text-white/70 text-xs font-bold hover:bg-white/10 cursor-pointer">Cancelar</button>
          <button onClick={submit} disabled={busy || !chans.size || total > free}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/35 text-emerald-400 text-xs font-bold hover:bg-emerald-500/25 cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Iniciar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Bots ───────────────────────── */

function BotsTab({ accounts, online, loading }: { accounts: BotAccountRow[]; online: OnlineBotRow[]; loading: boolean }) {
  const [open, setOpen] = useState<BotAccountRow | null>(null);
  const live = useMemo(() => new Map(online.map(b => [b.characterIdx, b])), [online]);
  const isOn = (a: BotAccountRow) => a.online || (a.characterIdx != null && live.has(a.characterIdx));

  const cols: DataTableColumn<BotAccountRow>[] = [
    {
      key: 'account', label: 'Conta', width: '1.2fr',
      cell: (a) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{a.accountId}</p>
          <p className="text-[10px] text-white/35 font-mono">UserNum {a.userNum}</p>
        </div>
      ),
    },
    {
      key: 'name', label: '1º personagem', width: '1.6fr',
      cell: (a) => (
        <div className="min-w-0">
          <p className="text-sm text-white truncate">{a.name || '—'}</p>
          <p className="text-[10px] text-white/40 truncate">{formatClass(a.charClass)} · Lv {a.level ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'power', label: 'Poder de combate', width: '140px', align: 'right',
      cell: (a) => <span className="text-sm font-bold text-amber-300 font-mono">{a.combatPower.toLocaleString('pt-BR')}</span>,
    },
    {
      key: 'status', label: 'Status', width: '150px',
      cell: (a) => {
        const b = a.characterIdx != null ? live.get(a.characterIdx) : undefined;
        return isOn(a)
          ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400">
              <Circle size={8} className="fill-emerald-400" /> ON{b ? ` · CH${b.channel}` : ''}</span>
          : <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white/35">
              <Circle size={8} /> OFF</span>;
      },
    },
  ];

  if (open) return <BotDetail acc={open} on={isOn(open)} live={open.characterIdx != null ? live.get(open.characterIdx) : undefined} onBack={() => setOpen(null)} />;

  return (
    <DataTable<BotAccountRow>
      columns={cols}
      rows={accounts}
      rowKey={(a) => a.userNum}
      onRowClick={(a) => a.characterIdx && setOpen(a)}
      loading={loading}
      empty="Nenhuma conta botsvr encontrada"
      mobileTitle={(a) => <span>{a.accountId} · {a.name || '—'}</span>}
      mobileSubtitle={(a) => `${isOn(a) ? 'ON' : 'OFF'} · Poder ${a.combatPower.toLocaleString('pt-BR')}`}
    />
  );
}

function BotDetail({ acc, on, live, onBack }: {
  acc: BotAccountRow; on: boolean; live?: OnlineBotRow; onBack: () => void;
}) {
  const nation = acc.nation ? NATIONS_PT[acc.nation] : null;
  const worldName = (id?: number | null) => WORLDS.find(w => w.id === id)?.name || (id ? `Mapa ${id}` : '—');
  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-bold text-white/60 hover:text-white mb-3 cursor-pointer">
        <ChevronLeft size={14} /> Voltar à lista
      </button>

      <div className="surface-card rounded-xl p-4 mb-4 flex flex-wrap items-center gap-x-8 gap-y-3">
        <div>
          <p className="text-lg font-bold text-white">{acc.name}</p>
          <p className="text-xs text-white/50">{acc.accountId} · CharIdx {acc.characterIdx} · {formatClass(acc.charClass)} · Lv {acc.level ?? '—'}</p>
        </div>
        {nation && <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${nation.classes}`}>{nation.name}</span>}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Status</p>
          <p className={`text-sm font-bold ${on ? 'text-emerald-400' : 'text-white/40'}`}>
            {on ? `ON${live ? ` · CH${live.channel} · ${worldName(live.mapId)} (${live.x},${live.y})` : ''}` : 'OFF'}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold flex items-center gap-1"><Zap size={10} /> Poder de combate</p>
          <p className="text-sm font-bold text-amber-300 font-mono">{acc.combatPower.toLocaleString('pt-BR')}</p>
        </div>
        {on && <p className="text-[11px] text-amber-300/80 w-full">Bot online — para editar itens desligue-o (a edição exige o personagem offline).</p>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="surface-card rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3">Equipamento</h3>
          <ItemsView source="equipment" id={acc.characterIdx!} classId={acc.charClass ?? undefined} />
        </div>
        <div className="surface-card rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3">Inventário</h3>
          <ItemsView source="inventory" id={acc.characterIdx!} />
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {children}
    </button>
  );
}
